import { locales, type Locale } from 'shared/locales'

/**
 * The URL segment each locale is published under, and the single source for that set: the router's rewrite, the
 * hreflang links, the sitemap and the language switcher all read it, so what the site advertises cannot drift
 * from what it serves. Short codes because the region is implied and `/es/` is what a visitor types.
 */
export const localeSegments: Record<Locale, string> = {
  'es-ES': 'es',
  'ca-ES': 'ca',
  'en-US': 'en',
  'pt-BR': 'pt',
}

const localeBySegment = new Map<string, Locale>(locales.map((locale) => [localeSegments[locale], locale]))

/**
 * The public, indexable pages — the ONLY paths that carry a locale segment. The authenticated app, `/trpc`, `/api`
 * and the AUTH SCREENS are deliberately absent: a door into the product is not a page anyone ranks for, so a prefix
 * buys no signal and publishes more URLs into the app. Auth keeps its meta, self-canonicalises, and resolves
 * language as it always did (cookie, then `Accept-Language`).
 */
export const SEO_PATHS = ['/', '/terms-of-use', '/privacy-policy', '/cookies-policy', '/docs'] as const

const seoPaths = new Set<string>(SEO_PATHS)

/** Normalised to the form `SEO_PATHS` is written in: no trailing slash, `/` for the root. */
const normalize = (pathname: string) => pathname.replace(/\/+$/, '') || '/'

export const isSeoPath = (pathname: string) => seoPaths.has(normalize(pathname))

/**
 * The locale a URL DECLARES, and the internal path below it.
 *
 * A leading segment only counts as a locale when what remains is a public page: `/es` is Spanish, `/employees`
 * is a section of the app, and a hypothetical `/en/employees` is just a 404 rather than a second way in.
 */
export function splitLocalePath(pathname: string): { locale?: Locale; path: string } {
  const match = /^\/([^/]+)(\/.*)?$/.exec(pathname)
  const segment = match?.[1]
  const locale = segment ? localeBySegment.get(segment) : undefined
  if (!locale) return { path: pathname }
  const path = normalize(match?.[2] ?? '/')
  return seoPaths.has(path) ? { locale, path } : { path: pathname }
}

/** A public page's URL in one locale. Anything else comes back untouched — only public pages are prefixed. */
export function localePath(pathname: string, locale: Locale): string {
  const path = normalize(pathname)
  if (!seoPaths.has(path)) return pathname
  return `/${localeSegments[locale]}${path === '/' ? '' : path}`
}
